import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Rocket, Users, TrendingUp, Globe, Star, Video, Layers, MousePointerClick, LineChart, ArrowRight, Quote, Play, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import { api, LOGO_URL } from "@/lib/api";
import PitchFlipCard from "@/components/PitchFlipCard";

const stats = [
  { icon: Rocket, value: "2,850+", label: "Startups" },
  { icon: Users, value: "18,500+", label: "Active Investors" },
  { icon: TrendingUp, value: "$320M+", label: "Total Invested" },
  { icon: Globe, value: "120+", label: "Countries" },
  { icon: Star, value: "4.8/5", label: "Platform Rating" },
];

const steps = [
  { icon: Video, title: "Watch 1-Minute Pitches", desc: "Discover startups through quick, engaging videos." },
  { icon: Layers, title: "Flip to See Full Deck", desc: "Click to flip the card and explore detailed pitch decks." },
  { icon: MousePointerClick, title: "Engage & Connect", desc: "Like, show interest, or share with your network." },
  { icon: LineChart, title: "Invest in the Future", desc: "Connect with founders and be part of the next big success." },
];

const testimonials = [
  { quote: "EBN has completely changed the way I discover and evaluate startups. One-minute pitches save so much time!", name: "Sarah Johnson", role: "Angel Investor", img: "https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=100&q=80&fm=jpg&crop=faces&fit=crop" },
  { quote: "We raised our seed round within weeks of posting on EBN. The right investors saw our pitch instantly.", name: "Rahul Verma", role: "Founder, EcoWatt", img: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=100&q=80&fm=jpg&crop=faces&fit=crop" },
  { quote: "The Shark Tank events on EBN are incredible. Real conversations, real opportunities.", name: "Michael Chen", role: "VC Partner", img: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=100&q=80&fm=jpg&crop=faces&fit=crop" },
];

const features = [
  { title: "Live Webinars", desc: "Join exclusive webinars and connect with founders and investors in real-time.", cta: "Join Live Now", to: "/live", img: "https://customer-assets-gfyr7b9c.emergentagent.net/job_pitch-netflix-1/artifacts/0wfnyzww_Live%20Webinars.png" },
  { title: "Events & Shark Tank", desc: "Register for upcoming events and pitch live to a panel of top investors.", cta: "Explore Events", to: "/events", img: "https://customer-assets-gfyr7b9c.emergentagent.net/job_pitch-netflix-1/artifacts/k5gyzmq2_Events%20%26%20Shark%20Tank.png" },
  { title: "For Founders", desc: "Upload your 1-minute pitch and get discovered by thousands of investors.", cta: "Raise Capital Now", to: "/pricing", img: "https://customer-assets-gfyr7b9c.emergentagent.net/job_pitch-netflix-1/artifacts/zocmx49a_For%20Founders.png" },
];

export default function Home() {
  const [featured, setFeatured] = useState(null);
  const [bestWeek, setBestWeek] = useState([]);
  const [email, setEmail] = useState("");

  useEffect(() => {
    api.get("/pitches/featured").then((r) => setFeatured(r.data)).catch(() => {});
    api.get("/pitches/best-week").then((r) => setBestWeek(r.data)).catch(() => {});
  }, []);

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email.includes("@")) { toast.error("Please enter a valid email."); return; }
    await api.post("/newsletter/subscribe", { email });
    toast.success("You're in! Exclusive opportunities coming your way.");
    setEmail("");
  };

  return (
    <main className="relative">
      <div className="noise-overlay" />
      {/* HERO */}
      <section className="relative min-h-[calc(100vh-4rem)] mt-16 flex items-center py-10 overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(245,179,1,0.07),transparent_55%)]" />
        <div className="max-w-7xl w-full mx-auto px-4 sm:px-6 grid lg:grid-cols-2 gap-14 items-center relative">
          <div className="text-left fade-up">
            <h1 className="font-bold tracking-tight leading-[1.1]">
              <span className="block text-4xl sm:text-5xl lg:text-6xl">Discover Tomorrow's Unicorns.</span>
              <span className="gold-gradient-text block text-2xl sm:text-3xl lg:text-4xl mt-4">Before the World Does.</span>
            </h1>
            <p className="mt-6 text-base md:text-lg text-zinc-400 max-w-md leading-relaxed">
              EBN is the ultimate marketplace for startup investments. Discover, connect, and invest in the next big thing.
            </p>
            <div className="mt-8 flex flex-wrap gap-4">
              <Link to="/pitches" data-testid="hero-explore-btn" className="inline-flex items-center gap-2 px-7 py-3 rounded-full bg-[#F5B301] text-black text-sm font-bold hover:bg-[#D99E01] transition-colors">
                Explore Pitches <Play size={14} fill="black" />
              </Link>
              <a href="#how-it-works" data-testid="hero-how-btn" className="inline-flex items-center gap-2 px-7 py-3 rounded-full border border-white/15 text-sm font-semibold text-white hover:border-[#F5B301]/60 hover:text-[#F5B301] transition-colors">
                How It Works <ArrowRight size={14} />
              </a>
            </div>
            <div className="mt-12 grid grid-cols-2 sm:grid-cols-4 gap-6">
              {stats.slice(0, 4).map((s) => (
                <div key={s.label} className="flex items-center gap-2.5">
                  <s.icon size={20} className="text-[#F5B301] shrink-0" />
                  <div>
                    <p className="font-heading font-bold text-white text-sm">{s.value}</p>
                    <p className="text-[11px] text-zinc-500">{s.label}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="relative fade-up" style={{ animationDelay: "0.15s" }}>
            {featured && (
              <div className="relative max-w-lg" data-testid="hero-featured-pitch">
                <span className="absolute -top-3 left-4 z-10 text-[10px] font-bold tracking-widest uppercase bg-[#F5B301] text-black px-3 py-1 rounded-full">Featured Pitch</span>
                <PitchFlipCard pitch={featured} />
              </div>
            )}
          </div>
        </div>
        <button
          data-testid="scroll-hint-btn"
          aria-label="Scroll down to explore"
          onClick={() => document.getElementById("best-week")?.scrollIntoView({ behavior: "smooth" })}
          className="absolute bottom-6 left-1/2 -translate-x-1/2 flex flex-col items-center gap-1.5 text-zinc-500 hover:text-[#F5B301] transition-colors"
        >
          <span className="text-[10px] uppercase tracking-[0.25em] font-semibold">Scroll</span>
          <span className="scroll-cue w-8 h-12 rounded-full border border-current flex items-start justify-center pt-2">
            <ChevronDown size={16} />
          </span>
        </button>
      </section>

      {/* BEST OF WEEK */}
      <section id="best-week" className="max-w-7xl mx-auto px-4 sm:px-6 pt-6 pb-16">
        <div className="rounded-2xl bg-[#0d0d0d] border border-white/5 p-6 min-w-0" data-testid="best-week-section">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl sm:text-2xl font-semibold text-left">Best Pitches of the Week</h2>
            <Link to="/pitches" data-testid="best-week-viewall" className="text-xs font-semibold text-[#F5B301] hover:underline">View All</Link>
          </div>
          <div className="flex gap-5 overflow-x-auto hide-scrollbar pb-2">
            {bestWeek.map((p) => (
              <div key={p.id} className="min-w-[340px] w-[340px] shrink-0">
                <PitchFlipCard pitch={p} />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FEATURE CARDS */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 pb-16 grid md:grid-cols-3 gap-8">
        {features.map((f) => (
          <div key={f.title} data-testid={`feature-card-${f.title.toLowerCase().replace(/[^a-z]+/g, "-")}`} className="relative rounded-2xl overflow-hidden border border-white/10 min-h-[260px] flex flex-col justify-end p-7 text-left gold-glow-hover">
            <img src={f.img} alt={f.title} className="absolute inset-0 w-full h-full object-cover opacity-50" />
            <div className="absolute inset-0 bg-gradient-to-t from-black via-black/60 to-transparent" />
            <div className="relative">
              <h3 className="text-xl font-semibold text-white">{f.title}</h3>
              <p className="mt-2 text-sm text-zinc-400 leading-relaxed">{f.desc}</p>
              <Link to={f.to} className="inline-block mt-4 px-5 py-2 rounded-md border border-[#F5B301]/60 text-[#F5B301] text-xs font-semibold hover:bg-[#F5B301] hover:text-black transition-colors">
                {f.cta}
              </Link>
            </div>
          </div>
        ))}
      </section>

      {/* STATS BAND */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 pb-20">
        <div className="rounded-2xl bg-[#0d0d0d] border border-white/5 py-10 px-6 grid grid-cols-2 md:grid-cols-5 gap-8" data-testid="stats-band">
          {stats.map((s) => (
            <div key={s.label} className="flex flex-col items-center gap-2">
              <s.icon size={26} className="text-[#F5B301]" />
              <p className="font-heading font-bold text-2xl text-white">{s.value}</p>
              <p className="text-xs text-zinc-500">{s.label}</p>
            </div>
          ))}
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section id="how-it-works" className="max-w-7xl mx-auto px-4 sm:px-6 pb-20" data-testid="how-it-works-section">
        <h2 className="text-3xl sm:text-4xl font-semibold text-center mb-12">How EBN Works</h2>
        <div className="rounded-2xl bg-[#0d0d0d] border border-white/5 p-10 grid md:grid-cols-4 gap-10">
          {steps.map((s, i) => (
            <div key={s.title} className="flex flex-col items-center text-center gap-3">
              <div className="w-14 h-14 rounded-2xl border border-[#F5B301]/30 bg-[#F5B301]/5 flex items-center justify-center">
                <s.icon size={24} className="text-[#F5B301]" />
              </div>
              <span className="w-6 h-6 rounded-full bg-[#1c1c1e] border border-white/10 text-[11px] font-bold flex items-center justify-center text-zinc-400">{i + 1}</span>
              <h3 className="text-base font-semibold text-white">{s.title}</h3>
              <p className="text-xs text-zinc-500 leading-relaxed">{s.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 pb-20" data-testid="testimonials-section">
        <h2 className="text-3xl sm:text-4xl font-semibold text-center mb-12">Loved by Investors & Founders</h2>
        <div className="grid md:grid-cols-3 gap-8">
          {testimonials.map((t) => (
            <div key={t.name} className="rounded-2xl bg-[#0d0d0d] border border-white/5 p-7 text-left gold-glow-hover">
              <Quote size={22} className="text-[#F5B301] mb-4" />
              <p className="text-sm text-zinc-300 leading-relaxed">{t.quote}</p>
              <div className="mt-6 flex items-center gap-3">
                <img src={t.img} alt={t.name} className="w-10 h-10 rounded-full object-cover" />
                <div>
                  <p className="text-sm font-semibold text-white">— {t.name}</p>
                  <p className="text-xs text-zinc-500">{t.role}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* NEWSLETTER */}
      <section className="relative border-t border-white/5 bg-[#0a0a0a] py-16 overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_right,rgba(245,179,1,0.06),transparent_60%)]" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 flex flex-col lg:flex-row items-center justify-between gap-8 relative">
          <div className="text-left">
            <h2 className="text-3xl sm:text-4xl font-semibold">Stay Ahead. Get Exclusive Opportunities.</h2>
            <p className="mt-3 text-sm text-zinc-500">Subscribe to get the best pitches, events, and insights straight to your inbox.</p>
          </div>
          <form onSubmit={subscribe} className="flex w-full max-w-md gap-0" data-testid="newsletter-form">
            <input
              data-testid="newsletter-email-input"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Enter your email"
              className="flex-1 bg-[#121212] border border-white/10 rounded-l-full px-5 py-3 text-sm text-white placeholder:text-zinc-600 focus:outline-none focus:border-[#F5B301]/50"
            />
            <button data-testid="newsletter-subscribe-btn" type="submit" className="px-6 py-3 rounded-r-full bg-[#F5B301] text-black text-sm font-bold hover:bg-[#D99E01] transition-colors">
              Subscribe
            </button>
          </form>
        </div>
      </section>
    </main>
  );
}
